import { stableHash } from './embedding'
import type { IntentChunk, WebMcpChunkPlan } from './types'

const MUTATING_KINDS = new Set([
  'operation',
  'style',
  'copy',
  'data',
  'actionflow',
  'navigation',
  'locale',
])

export function createWebMcpChunkPlan(rawInput: string, chunks: IntentChunk[]): WebMcpChunkPlan {
  const inputHash = stableHash(rawInput)
  const plan: WebMcpChunkPlan = {
    planId: `plan_${stableHash(`${inputHash}:${chunks.map((chunk) => chunk.id).join('|')}`)}`,
    inputHash,
    chunks,
    actions: [],
    blockers: [],
  }

  for (const chunk of chunks) {
    const candidate = chunk.mcpCandidates?.[0]
    if (!candidate) {
      if (chunk.kind !== 'target' && chunk.kind !== 'unknown') {
        plan.blockers.push({
          chunkId: chunk.id,
          reason: 'missing-tool',
          message: `No deterministic WebMCP tool is mapped for ${chunk.kind}.`,
        })
      }
      continue
    }

    if (chunk.confidence < 0.58) {
      plan.blockers.push({
        chunkId: chunk.id,
        reason: 'low-confidence',
        message: `Chunk "${chunk.rawText}" needs Qwen/WebMCP verification before execution.`,
      })
      continue
    }

    plan.actions.push({
      chunkIds: [chunk.id],
      tool: candidate.tool,
      paramsPreview: {
        canonicalId: chunk.canonicalId,
        kind: chunk.kind,
        cogoJsonPaths: chunk.cogoJsonPaths ?? [],
      },
      confidence: Math.min(chunk.confidence, candidate.score),
      requiresConfirmation: MUTATING_KINDS.has(chunk.kind),
      expectedChangedPaths: chunk.cogoJsonPaths ?? [],
    })
  }

  if (chunks.some((chunk) => chunk.kind === 'unknown')) {
    plan.blockers.push({
      chunkId: chunks.find((chunk) => chunk.kind === 'unknown')?.id ?? 'unknown',
      reason: 'knowledge-gap',
      message: 'No admitted local chunk mapping exists yet.',
    })
  }

  return plan
}
